import Utils, { event, isObject } from "../Utils.js";
import Auth from "../Auth";
import firebase from "firebase/app";
import "firebase/auth";
import { DESCRIBE_SENSES } from "../../../src/shared/constants.js";
import {
  createBenchmarkInterceptContext,
  maybeInterceptBenchmarkChunk,
  benchmarkStreamEnded,
  benchmarkStreamFailed,
} from "../components/BenchmarkTakeover/benchmarkIntercept";

const db = firebase.firestore();
const collection = db.collection("cards");

class Card {
  constructor(id = null, data, fetched = true) {
    if (fetched && !id) {
      throw new Error("ID is required from a fetched object");
    }

    // generate a stable clientId that can be used as a key b/c the id may be replaced later by the server
    this.clientId = Utils.generateId();

    if (fetched) {
      this.id = id;
    } else {
      this.id = Utils.generateId();
    }

    this.fetched = fetched;

    if (!data.source) {
      throw new Error("Data: source is required");
    }

    if (!data.createdAt) {
      throw new Error("Data: createdAt is required");
    }

    if (!data.content) {
      throw new Error("Data: content is required");
    }

    if (!data.documentId) {
      throw new Error(`Data: documentId is required for doc card ${this.id}`);
    }

    this.data = data;

    this.viewed = {};

    if (!(this.data.createdAt instanceof Date)) {
      // convert from firebase format
      this.data.createdAt = Utils.dateFromTimestamp(this.data.createdAt);
    }

    this.deleted = false;
    this.deletedItemsMemo = new Map();
    this.deletedItemsMemoSource = null;
  }

  // Given a source, what should be the prompt shown in summary be
  static summaryKey(source) {
    let key;

    switch (source) {
      case "multiverse":
      case "guidedWormhole":
      case "character":
      case "summary":
      case "tweetstorm":
      case "whatIf":
      case "expand":
      case "nextScene":
      case "twist":
      case "poem":
      case "analyze":
      case "rewrite":
      case "brainstorm":
      case "visualize":
      case "shrinkRay":
        key = "prompt";
        break;
      case "description":
        key = "object";
        break;
      default: // custom functions
        key = "prompt";
        break;
    }

    return key;
  }

  // Given a source, what should the name shown in UI be
  static name(source) {
    let name;

    switch (source) {
      case "character":
      case "summary":
      case "tweetstorm":
      case "expand":
      case "twist":
      case "description":
      case "poem":
      case "rewrite":
      case "brainstorm":
      case "visualize":
        name = source.charAt(0).toUpperCase() + source.slice(1);
        break;
      case "shrinkRay":
        name = "Shrink Ray";
        break;
      case "nextScene":
        name = "Scene Wormhole";
        break;
      case "whatIf":
        name = "What If";
        break;
      case "analyze":
        name = "Feedback";
        break;
      case "multiverse":
      case "guidedWormhole":
        name = "Wormhole";
        break;
      default:
        name = source.charAt(0).toUpperCase() + source.slice(1);
        break;
    }

    return name;
  }

  get source() {
    return Card.name(this.data.source);
  }

  get prompt() {
    return this.data[Card.summaryKey(this.data.source)];
  }

  mergeData(data) {
    this.data = Object.assign(this.data, data);
  }

  setFetched(id) {
    if (!id) {
      throw new Error("Must provide an ID");
    }

    this.id = id;
    this.fetched = true;

    console.log("Card is now fetched: ", id);
  }

  // deletes the entire card from firebase
  async delete() {
    await collection.doc(this.id).delete();
    this.deleted = true;
  }

  async softDelete() {
    if (this.fetched) {
      await collection.doc(this.id).update({
        deleted: true,
      });
    }
    this.data.deleted = true;
  }

  hasItems() {
    let isCompletionEmpty = (completion) => {
      if (isObject(completion)) {
        return false;
      }

      const hasContent = completion && completion.length > 0 && completion.replace(/\s/g, "").length > 0;
      if (!hasContent) {
        return true;
      }

      return false;
    };

    switch (this.data.source) {
      case "plugin":
      case "rewrite":
        return this.data.content.some(item => {
          return !isCompletionEmpty(item.text) && !this.isContentDeleted(item);
        });
      case "description":
        for (const { value: senseKey } of DESCRIBE_SENSES) {
          const senseContent = this.data.content[senseKey];
          if (!senseContent || !senseContent.length) {
            continue;
          }

          let isDeleted;

          if (senseKey === 'metaphor') {
            isDeleted = senseContent.every(senseContentItem => this.isContentDeleted(senseContentItem));
          } else {
            isDeleted = this.isContentDeleted(senseContent.join("\n\n"));
          }

          if (!isDeleted) {
            return true;
          }
        }
        return false;
      case "multiverse":
      case "guidedWormhole":
      case "poem":
      case "whatIf":
      case "visualize":
      case "summary":
      case "analyze":
      case "expand":
      case "command":
      case "quickEditRestore":
      case "twist":
      case "character":
        return this.data.content.some(item => {
          return !isCompletionEmpty(item) && !this.isContentDeleted(item);
        });
      case "brainstorm":
        return !this.isContentDeleted();
      default:
        return this.data.content.some(item => {
          return !isCompletionEmpty(item) && !this.isContentDeleted(item);
        });
    }
  }

  isContentDeleted(content) {
    const deletedItems = this.data.deletedItems;
    // Brainstorm displays one aggregate card, including when its ideas are empty.
    // Preserve the whole-card meaning of legacy deletedItems entries.
    if (this.data.source === "brainstorm") {
      return !!this.data.deleted || !!deletedItems?.length;
    }
    if (!content || !deletedItems?.length) {
      return false;
    }

    // The memo is only valid for the deletedItems array it was built from; deletedItems
    // is always replaced wholesale, so a changed reference means cached results are stale
    // (SW-9573: a render mid-delete could otherwise cache "not deleted" permanently).
    if (this.deletedItemsMemoSource !== deletedItems) {
      this.deletedItemsMemo.clear();
      this.deletedItemsMemoSource = deletedItems;
    }

    if (this.deletedItemsMemo.has(content)) {
      return this.deletedItemsMemo.get(content);
    }

    const path = this.pathFromContent(content);

    const [key, idx] = path;
    const isDeleted =
      deletedItems.find(({ key: choiceKey, index: choiceIndex }) => {
        return choiceKey === key && choiceIndex === idx;
      }) !== undefined;

    this.deletedItemsMemo.set(content, isDeleted);
    return isDeleted;
  }

  async deleteAtPath(path) {
    const [key, index] = path;
    if (index === null || index === undefined || index < 0) {
      throw new Error(`Cannot delete card content at invalid path [${key}, ${index}] for card ${this.id}`);
    }

    if (this.data.source === "brainstorm") {
      // One persisted write; keep the aggregate visible until soft deletion succeeds.
      await this.softDelete();
      return;
    }

    if (!this.data.deletedItems) {
      this.data.deletedItems = [];
    }

    const previousDeletedItems = this.data.deletedItems;
    const updatedDeletedItems = [
      ...previousDeletedItems,
      {
        key,
        index,
      },
    ];

    // Apply locally before the Firestore round trip: the confirming click also fires a
    // document-level mousedown that re-renders the card list while the write is still in
    // flight, and that render must already see the item as deleted (SW-9573).
    this.data.deletedItems = updatedDeletedItems;

    // A card that was never persisted (e.g. the stream errored before the server sent
    // the card id) has no Firestore doc to update — writing to this.id would reject with
    // NOT_FOUND on every attempt. The local deletion is all there is (SW-9573).
    if (this.fetched) {
      try {
        await collection.doc(this.id).update({ deletedItems: updatedDeletedItems });
      } catch (error) {
        this.data.deletedItems = previousDeletedItems;
        throw error;
      }
    }

    if (!this.hasItems()) {
      await this.softDelete();
    }
  }

  // Emits a view event to server for all cards that haven't already been viewed in this session
  async emitViewEvents() {
    // HACK: delay a couple seconds to make sure in the case where cards are updated that the server has time to update it (ie. Filter)
    setTimeout(async () => {
      if (!this.fetched) {
        throw "Card must be fetched in order to emit a view event";
      }

      let events = [];

      // send up view events for all the cards
      if (this.data.source === "description") {
        for (let key in this.data.content) {
          let path = [key, 0];
          if (!this.viewed[path]) {
            events.push({ eventType: "view", cardId: this.id, path: path });
            this.viewed[path] = true;
          }
        }
      } else {
        for (let i = 0; i < this.data.content.length; i++) {
          let path = [null, i];
          if (!this.viewed[path]) {
            events.push({ eventType: "view", cardId: this.id, path: [null, i] });
            this.viewed[path] = true;
          }
        }
      }

      if (events.length > 0) {
        await event(events);
      }
    }, 2000);
  }

  // Path is an array of form
  // [key, index]
  // key: is the top level key where the content to be removed is (optional. if not specified we assume the "content" key is an array
  // index: index into array of choices
  async removeContent(path) {
    let key = path[0];
    let index = path[1];

    if (!key) {
      if (this.data.source === "description") {
        throw new Error("Key must be specified when removing content from a description Card");
      }
      // remove from array
      this.data.content.splice(index, 1);
    } else {
      if (this.data.source !== "description") {
        throw new Error("Cannot remove from a content path unless Card is a description");
      }

      if (index === null) {
        this.data.content[key] = null;
      } else {
        this.data.content[key].splice(index, 1);
      }
    }

    if (this.isEmpty()) {
      // delete the entire card
      await this.delete();
    } else {
      await collection.doc(this.id).update({ content: this.data.content });
    }
  }

  contentFromPath(path) {
    let [key, idx] = path;

    let content;

    if (key) {
      if (!this.data.content[key]) {
        return null;
      } else {
        return this.data.content[key][idx];
      }
    } else {
      return this.data.content[idx];
    }
  }

  pathFromContent(content) {
    let path;
    let idx, key;

    switch (this.data.source) {
      case "multiverse":
      case "guidedWormhole":
      case "character":
      case "summary":
      case "shrinkRay":
      case "tweetstorm":
      case "expand":
      case "twist":
      case "poem":
      case "whatIf":
      case "analyze":
      case "rewrite":
      case "list":
      case "visualize":
        idx = this.data.content.findIndex((choice) => {
          return choice === content;
        });

        path = [null, idx];
        break;
      case "brainstorm": {
        const firstLineContent = content.split("\n")[0];
        const idx = this.data.content.findIndex((choice) => {
          return choice === firstLineContent;
        });
        path = [null, idx];
        break;
      }
      case "description":
        // special case: for description we show cards that concatenate senses together, so we need to search by only the first line

        let firstLineContent = content.split("\n")[0];

        let keys = Object.keys(this.data.content);
        keys.forEach((k) => {
          let i = this.data.content[k]?.findIndex((choice) => {
            return choice === firstLineContent;
          });

          if (i !== -1) {
            key = k;
            idx = i;
          }
        });
        path = [key, idx];
        break;
      default:
        // Custom functions
        idx = this.data.content.findIndex((choice) => {
          return choice === content;
        });

        path = [null, idx];
        break;
    }

    return path;
  }

  isEmpty() {
    if (!this.data.content) {
      return true;
    }

    if (Object.keys(this.data.content).length === 0) {
      return true;
    }

    let empty = true;

    for (let key in this.data.content) {
      if (this.data.content[key] && Object.keys(this.data.content[key]).length !== 0) {
        empty = false;
      }
    }

    return empty;
  }

  hasLiked(path) {
    let likes = this.data.likes || [];

    let idx = likes.findIndex((like) => {
      return like.path[0] === path[0] && like.path[1] === path[1];
    });

    if (idx == -1) {
      return null;
    } else {
      return likes[idx].value;
    }
  }

  getUserFeedback(path) {
    let likes = this.data.likes || [];

    let idx = likes.findIndex((like) => {
      return like.path[0] === path[0] && like.path[1] === path[1];
    });

    if (idx == -1) {
      return null;
    } else {
      return likes[idx].userFeedback;
    }
  }

  async setUserFeedback(path, userFeedback) {
    if (!this.fetched) {
      throw new Error("Attempt to set user feedback on a card in a card set that hasn't been fetched yet");
    }

    let content = this.contentFromPath(path);

    if (content === null || content === undefined) {
      throw new Error(`Attempt to set user feedback on content that doesn't exist: ${path}`);
    }

    let likes = this.data.likes || [];

    // find the like object
    let idx = likes.findIndex((like) => {
      return like.path[0] === path[0] && like.path[1] === path[1];
    });

    // update the user feedback
    likes[idx].userFeedback = userFeedback;

    this.data.likes = likes;

    // save the new likes on the card
    await collection.doc(this.id).update({
      likes: likes,
    });

    event({ eventType: "feedback", cardId: this.id, path: path, eventValue: userFeedback });

    return true;
  }

  async removeLike(path) {
    if (!this.fetched) {
      throw new Error("Attempt to like a card in a card set that hasn't been fetched yet");
    }

    let content = this.contentFromPath(path);

    if (!content) {
      throw new Error(`Attempt to remove like from content that doesn't exist: ${path}`);
    }

    let likes = this.data.likes || [];

    let idx = likes.findIndex((like) => {
      return like.path[0] === path[0] && like.path[1] === path[1];
    });

    if (idx != -1) {
      let value = likes[idx].value;
      likes.splice(idx, 1);
      this.data.likes = likes;

      await collection.doc(this.id).update({
        likes: likes,
      });

      let eventType = value == 1 ? "unlike" : "undislike";
      event({ eventType: eventType, cardId: this.id, path: path });
    } else {
      // we never liked it..
      return true;
    }
  }

  async createLike(path, value) {
    let alreadyDisliked = false;

    if (!this.fetched) {
      throw new Error("Attempt to like a card in a card set that hasn't been fetched yet");
    }

    let content = this.contentFromPath(path);

    if (content === null || content === undefined) {
      throw new Error(`Attempt to like content that doesn't exist: ${path}`);
    }

    if (value !== -1 && value !== 1) {
      throw new Error(`Attempt to like with an invalid value: ${value}`);
    }

    let likes = this.data.likes || [];

    // check to see if we've already liked it
    let idx = likes.findIndex((like) => {
      return like.path[0] === path[0] && like.path[1] === path[1];
    });

    if (idx != -1) {
      if (likes[idx].value == value) {
        // if it's already the same, do nothing
        return true;
      } else {
        // if not, then save new value in its place
        likes[idx].value = value;

        // send event to undo the previous liking
        let eventType = value == 1 ? "undislike" : "unlike";
        event({ eventType: eventType, cardId: this.id, path: path });
      }
    } else {
      likes.push({
        value: value,
        path: path,
      });
    }

    this.data.likes = likes;

    // save the new likes on the card
    await collection.doc(this.id).update({
      likes: likes,
    });

    let eventType = value == 1 ? "like" : "dislike";
    event({ eventType: eventType, cardId: this.id, path: path });

    return true;
  }

  streamWithIdToken(url, params, signal, delay, onUpdate, onError, onDone) {
    let submitting = true;

    // Benchmark interception (Write surface): the server may turn this
    // generation into a blinded two-sample comparison by emitting an early
    // `benchmarkSessionId` chunk. From then on chunks are routed into the
    // benchmark takeover store instead of this card. The request body/url are
    // captured up front (before Auth mutates params) so "Write Again" can
    // replay the identical generation with benchmarkRetry: true.
    const replayUrl = url;
    const replayParams = { ...params };
    const replayBenchmarkGeneration = () => {
      const controller = new AbortController();
      const abort = () => controller.abort();
      const retryCtx = createBenchmarkInterceptContext({ surface: "write", onAbort: abort, precedingText: this.benchmarkPrecedingText || "" });
      retryCtx.onWriteAgain = replayBenchmarkGeneration;
      retryCtx.expectSession = true;
      Auth.streamWithIdToken(
        replayUrl,
        { ...replayParams, benchmarkRetry: true },
        controller.signal,
        (chunk) => {
          maybeInterceptBenchmarkChunk(chunk, retryCtx);
        },
        () => benchmarkStreamEnded(retryCtx),
        (error) => benchmarkStreamFailed(retryCtx, error)
      );
      // Lets the takeover stop the replay before its session is announced.
      return abort;
    };
    // This request also needs its own cancellation handle: the takeover removes
    // its placeholder, so the card UI cannot cancel it when the writer leaves.
    const requestController = new AbortController();
    const abortRequest = () => requestController.abort(signal?.reason);
    if (signal?.aborted) abortRequest();
    else signal?.addEventListener("abort", abortRequest, { once: true });
    const detachAbort = () => signal?.removeEventListener("abort", abortRequest);
    const benchmarkCtx = createBenchmarkInterceptContext({
      surface: "write",
      onAbort: abortRequest,
      onWriteAgain: replayBenchmarkGeneration,
      card: this,
      precedingText: this.benchmarkPrecedingText || "",
    });

    // Pull from the choices array as its buffered up from the streaming
    // one second at a time. This avoids re-rendering on every single token that
    // we get back.
    let updateData = () => {
      onUpdate(this);

      // only look for more changes if we're still streaming back
      if (submitting) {
        setTimeout(updateData, delay);
      }
    };

    setTimeout(updateData, delay);

    return Auth.streamWithIdToken(
      url,
      params,
      requestController.signal,
      (chunk) => {
        if (maybeInterceptBenchmarkChunk(chunk, benchmarkCtx)) return;

        let content = chunk.content;

        //console.log(chunk);
        if (content) {
          if ("index" in content) {
            if (!this.data.content) {
              this.data.content = [];
            }

            let index = content.index;
            let text = content.text;

            if (Object.keys(content).length > 2) {
              // this is an array of key-value objects (rewrite)

              // get rid of the known fields
              delete content.index;
              delete content.text;

              // maybe initialize the object
              if (!this.data.content[index]) {
                this.data.content[index] = {};
              }

              // apply or append the text
              if (!this.data.content[index].text) {
                this.data.content[index].text = text;
              } else {
                this.data.content[index].text += text;
              }

              // merge in rest of keys
              Object.assign(this.data.content[index], content);
            } else {
              // this is a first level array (wormhole, etc)

              if (!this.data.content[index]) {
                this.data.content[index] = text;
              } else {
                this.data.content[index] += text;
              }
            }
          } else {
            // this is a set of arrays (ie. description)

            if (!this.data.content) {
              this.data.content = {};
            }

            // parse the contents and merge into streaming content
            Object.keys(content).forEach((key) => {
              let index = content[key].index;
              let text = content[key].text;

              if (!this.data.content[key]) {
                this.data.content[key] = [];
              }

              if (!this.data.content[key][index]) {
                this.data.content[key][index] = text;
              } else {
                this.data.content[key][index] += text;
              }
            });
          }

          delete chunk.content;
        }

        if (chunk.prompt) {
          // overwrites client prompt if new one sent down
          this.data.prompt = chunk.prompt;
          delete chunk.prompt;
        }

        // if an id comes down, then we know its on the server
        if (chunk.id) {
          this.setFetched(chunk.id);
          delete chunk.id;
        }

        // Merge the rest of the keys
        this.data = Object.assign(this.data, chunk);

        if (chunk.error) {
          onError(chunk.error);
          submitting = false;
        }
      },
      () => {
        detachAbort();
        submitting = false;
        // Both benchmark samples finished streaming → reveal in the takeover.
        benchmarkStreamEnded(benchmarkCtx);
        onDone();
      },
      (error) => {
        detachAbort();
        benchmarkStreamFailed(benchmarkCtx, error);
        onError(error);
        submitting = false;
      }
    );
  }

  // Streams the contents (in standard card format) for demo purposes (no backend)
  demoStream(contents, params, onUpdate, onDone) {
    let submitting = true;

    // Pull from the choices array as its buffered up from the streaming
    // one second at a time. This avoids re-rendering on every single token that
    // we get back.
    let updateData = () => {
      onUpdate(this);

      // only look for more changes if we're still streaming back
      if (submitting) {
        setTimeout(updateData, 1000);
      }
    };

    setTimeout(updateData, 1000);

    let chunks = [];

    let chunkify = (array, key) => {
      let splittedArray = array.map((content) => {
        return content.split(" ");
      });

      let longest = 0;
      let longestIdx = null;

      splittedArray.forEach((content, i) => {
        if (content.length > longest) {
          longest = content.length;
          longestIdx = i;
        }
      });

      splittedArray[longestIdx].forEach((content, i) => {
        // interleave
        for (let j = 0; j < splittedArray.length; j++) {
          if (splittedArray[j][i]) {
            let inner = {
              index: j,
              text: splittedArray[j][i] + " ",
            };

            let chunk = {};
            if (key) {
              chunk[key] = inner;
            } else {
              chunk = inner;
            }
            chunks.push({
              content: chunk,
            });
          }
        }
      });
    };

    // Split the contents into chunks
    if (Array.isArray(contents)) {
      // for wormhole
      chunkify(contents);
    } else {
      // for description
      Object.keys(contents).forEach((key) => {
        chunkify(contents[key], key);
      });
    }

    let work = (chunk) => {
      // The following is pretty much identical to the real stream, adding a fake delay
      return new Promise((resolve, reject) => {
        let delay = 10;
        setTimeout(() => {
          let content = chunk.content;

          //console.log(chunk);
          if (content) {
            if ("index" in content) {
              // this is a first level array (ie. wormhole)
              if (!this.data.content) {
                this.data.content = [];
              }

              let index = content.index;
              let text = content.text;

              if (!this.data.content[index]) {
                this.data.content[index] = text;
              } else {
                this.data.content[index] += text;
              }
            } else {
              // this is a set of arrays (ie. description)

              if (!this.data.content) {
                this.data.content = {};
              }

              // parse the contents and merge into streaming content
              Object.keys(content).forEach((key) => {
                let index = content[key].index;
                let text = content[key].text;

                if (!this.data.content[key]) {
                  this.data.content[key] = [];
                }

                if (!this.data.content[key][index]) {
                  this.data.content[key][index] = text;
                } else {
                  this.data.content[key][index] += text;
                }
              });
            }

            delete chunk.content;
          }

          // if an id comes down, then we know its on the server
          if (chunk.id) {
            this.setFetched(chunk.id);
            delete chunk.id;
          }

          // Merge the rest of the keys
          this.data = Object.assign(this.data, chunk);

          resolve();
          //}, Math.floor(Math.random() * 5) + 1);
        }, delay);
      });
    };

    // sequentially apply each chunk on after another, as if it were really streaming
    let result = chunks.reduce((acc, next) => {
      return acc.then(() => {
        return work(next);
      });
    }, Promise.resolve());

    // the last resolve
    result.then((e) => {
      submitting = false;
      onDone();
    });
  }
}

export default Card;
